import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../redux/hooks';
import { API } from '../api';
import { getUserDetails } from '../utils/orgName';

type Role = 'mentor' | 'mentee' | 'admin';

interface RoleCfg {
  mentor?: boolean;
  mentee?: boolean;
  admin?: boolean;
}

/**
 * Computes the union of optional fields that are ENABLED (mandatory or optional)
 * for the user in ANY of their programs. Mirrors the BE `getUserProfile` logic
 * that builds `settings` from a single program's `userOptionalFields`, but
 * considers EVERY program the user belongs to.
 *
 * Used during FTUE/onboarding so screens for fields required by any program get
 * shown — otherwise the user finishes onboarding without filling them and the
 * `MissingMandatoryFieldsTrigger` immediately re-prompts them on the dashboard.
 *
 * Data source: a SINGLE `groupsListForUser` call, which returns every program
 * the user belongs to (with role) AND that program's `userOptionalFields`
 * inline — resolved server-side in one batched query. This replaced an earlier
 * implementation that fetched the full group document once PER program
 * (`getGroupDetails` in a `Promise.all`), which for an admin in ~190 programs
 * fired ~190 `/group/<id>` requests. The call is cached (5 min) and is the same
 * one `fetchUserGroupList` already issues, so it usually costs zero extra
 * network.
 *
 * Returns a flat `{ [fieldName]: boolean }` map suitable for spreading into the
 * existing `settings` object consumed by the onboarding gating logic, plus a
 * `ready` flag.
 *
 * `ready` matters because the union resolves ASYNCHRONOUSLY. The onboarding
 * wizard must not assemble and render its page list until the union is in,
 * otherwise a late-resolving union reshuffles the list mid-flow: a stray page
 * flashes before the real first page, the progress bar jumps, and the current
 * step remounts and wipes in-progress input. `ready` flips true on both success
 * and error, so a failed fetch never leaves the wizard stuck on a loader.
 */
export default function useProgramFieldUnion(): { union: Record<string, boolean>; ready: boolean } {
  const userData = useAppSelector((s) => s.userProfile.data);
  const [union, setUnion] = useState<Record<string, boolean>>({});
  // The membership key the current `union` was resolved for (success or error).
  const [resolvedKey, setResolvedKey] = useState<string | null>(null);

  // Stable signature of the user's program membership — refetch only when the
  // set of programs/roles actually changes.
  const membershipKey = useMemo(() => {
    const ids = [
      ...((userData?.mentees as string[] | undefined) ?? []),
      ...((userData?.mentors as string[] | undefined) ?? []),
      ...((userData?.admins as string[] | undefined) ?? [])
    ].filter(Boolean);
    return Array.from(new Set(ids)).sort().join('|');
  }, [userData?.mentees, userData?.mentors, userData?.admins]);

  useEffect(() => {
    if (!membershipKey) {
      setUnion({});
      return undefined;
    }

    // No "already in flight for this key" guard here on purpose. The deps array
    // already dedupes re-runs for an unchanged key, and a guard combined with the
    // `cancelled` cleanup below wedges the wizard: when the effect is torn down
    // and re-run for the SAME key (StrictMode, HMR, a remount), the first run's
    // response is discarded as cancelled while the second run early-returns, so
    // `resolvedKey` is never set and `ready` stays false — an endless loader.
    let cancelled = false;
    const orgId = getUserDetails().orgId || '';

    API.groupsListForUser(orgId)
      .then((res: any) => {
        if (cancelled) return;
        const list = (res?.data?.data ?? res?.data ?? []) as Array<{
          role?: Role;
          userOptionalFields?: Record<string, RoleCfg | null> | null;
        }>;
        const out: Record<string, boolean> = {};
        for (const g of Array.isArray(list) ? list : []) {
          const cfg = g?.userOptionalFields;
          const role = g?.role;
          if (!cfg || typeof cfg !== 'object' || !role) continue;
          for (const [field, fieldCfg] of Object.entries(cfg)) {
            const c = fieldCfg as RoleCfg | null;
            if (c && typeof c === 'object' && (c as any)[role] !== undefined) {
              out[field] = true;
            }
          }
        }
        setUnion(out);
        setResolvedKey(membershipKey);
      })
      .catch(() => {
        if (!cancelled) {
          setUnion({});
          // Resolve anyway so a failed fetch does not hang the loader.
          setResolvedKey(membershipKey);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [membershipKey]);

  // Derived during render rather than set in an effect: the render in which a
  // new membership first appears (e.g. the profile just loaded) already reports
  // not-ready, so the wizard never paints a frame from a union-less page list.
  const ready = !membershipKey || resolvedKey === membershipKey;

  return { union, ready };
}
